import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import './DateSelector.css';
import { TZDate } from '@date-fns/tz';
import { differenceInCalendarDays, isAfter, isBefore } from 'date-fns';
import { CalendarDate } from '../../shared/types/triple.mts';
import { TOTAL_TURNS, ZERO_DAY } from './Constants.tsx';
import { usePuzzleIndex } from './PuzzleIndex.tsx';
import { GameState } from './State.tsx';
import { useLoginSession } from './SessionContext.tsx';

export const projectOntoEasternTime = (date: Date): TZDate => {
    return new TZDate(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 'America/New_York');
};

function DateSelector({ onChange, dateSelected }: { onChange: (date: Date) => void; dateSelected: Date | null }) {
    const { puzzleIndex } = usePuzzleIndex();
    const loginSession = useLoginSession();

    const classForDate = (date: Date) => {
        const calendarDate = CalendarDate.fromDate(date);
        const puzzleState = puzzleIndex.getPuzzleStateByDate(calendarDate, loginSession.getUserId());
        if (!puzzleState) {
            return 'unseen-tile';
        } else {
            // Handle deprecated COMPLETED state as well
            if (
                puzzleState.gameState === GameState.WON ||
                (puzzleState.gameState === GameState.COMPLETED && puzzleState.attemptIdx < TOTAL_TURNS)
            ) {
                return 'solved-tile';
            } else if (
                puzzleState.gameState === GameState.LOST ||
                (puzzleState.gameState === GameState.COMPLETED && puzzleState.attemptIdx >= TOTAL_TURNS)
            ) {
                return 'failed-tile';
            } else {
                return 'in-progress-tile';
            }
        }
    };

    const tileDisabled = ({ date }) => {
        // Project the date on to midnight eastern time
        const projectedDate = projectOntoEasternTime(date);
        const now = new TZDate(new Date(), 'America/New_York');
        if (differenceInCalendarDays(projectedDate, ZERO_DAY) < 0 || differenceInCalendarDays(projectedDate, now) > 0) {
            return true;
        }
        return false; // Enable all other dates
    };

    return (
        <div>
            <Calendar
                tileClassName={({ date }) => {
                    return !tileDisabled({ date }) ? classForDate(date) : '';
                }}
                onChange={(value, event) => {
                    if (!(value instanceof Date)) {
                        console.error('Range selection not supported.');
                        return;
                    }
                    onChange(projectOntoEasternTime(value));
                    return;
                }}
                value={dateSelected}
                minDetail='month'
                tileDisabled={tileDisabled}
                calendarType='gregory'
            />
        </div>
    );
}

export { DateSelector };
